import { type MarketClassification, type SportsData, type SubeventTeamData } from '~/components/BettingInsights/types';

import { Avatar as CxyAvatar, AvatarImage, PersonAvatar, TeamAvatars } from '../cxy/cxy.ts';

export type AvatarProps = {
  betName: string;
  homeOrAway?: 'home' | 'away';
  marketClassification?: MarketClassification;
  marketPropType: string;
  sportsData?: SportsData;
  teamData?: SubeventTeamData | null;
};

export const Avatar = ({
  betName,
  homeOrAway,
  marketClassification,
  marketPropType,
  sportsData,
  teamData,
}: AvatarProps) => {
  // If homeOrAway has been provided, attempt to display the teamData.[homeOrAway].badgeUrl, falling back to the default shield avatar if unavailable.
  if (homeOrAway) {
    return (
      <CxyAvatar size="small">
        <AvatarImage
          alt={`${teamData?.[homeOrAway].name || ''} team logo`}
          src={teamData?.[homeOrAway].badgeUrl || ''}
        />
      </CxyAvatar>
    );
  }

  switch (sportsData?.participant?.type) {
    case 'PLAYER':
      if (!sportsData.participant.imageUrl && sportsData.participant.team?.badgeUrl) {
        // If the player has no sportsData.participant.imageUrl, but they do have a participant.team.badgeUrl, then display the team badge.
        return (
          <CxyAvatar size="medium">
            <AvatarImage
              alt={`${sportsData.participant.team.name} team logo`}
              src={sportsData.participant.team.badgeUrl}
            />
          </CxyAvatar>
        );
      }
      return (
        // Otherwise, attempt to display their sportsData.participant.imageUrl, falling back to the default person avatar if unavailable.
        <PersonAvatar player={{ name: sportsData.participant.name, image: sportsData.participant.imageUrl || '' }} />
      );
    case 'TEAM':
      // Attempt to display the team sportsData.participant.badgeUrl, falling back to the default shield avatar if unavailable.
      return (
        <CxyAvatar size="medium">
          <AvatarImage alt={`${sportsData.participant.name} team logo`} src={sportsData.participant.badgeUrl || ''} />
        </CxyAvatar>
      );
  }

  // If none of the previous statements match, and we believe it is a player bet, then fall back to the default person avatar.
  if (marketClassification?.includes('PLAYER') || marketPropType.includes('Player'))
    return <PersonAvatar player={{ name: betName, image: '' }} />;

  // If none of the previous statements match, attempt to display the teamData badgeUrls, falling back to the default shield avatars if unavailable
  return (
    <TeamAvatars
      awayTeam={{ logo: teamData?.away?.badgeUrl || '', name: teamData?.away?.name || '' }}
      homeTeam={{ logo: teamData?.home?.badgeUrl || '', name: teamData?.home?.name || '' }}
      size="xsmall"
    />
  );
};
